/**
 * @file Makes every analytics answer on screen refetch when the numbers are
 * recomputed. Consumer: `useAnalyticsTabAnswers`, which calls it once. It
 * listens to the caller's `users/{uid}/analyticsSummaries/self` document,
 * which the analytics service rewrites after every cycle. The first
 * `computedAt` it sees is the baseline and starts nothing, so mounting never
 * fetches twice. A later move starts a random 0–30 s lag after the recompute,
 * so open pages do not all refetch at once; when it ends, one write puts the
 * latest value seen into `analyticsAnswersRefetch`. Moves inside the lag do
 * not restart it, and cleanup clears it.
 */
import { useContext, useEffect } from 'react';
import { subscribe } from 'valtio';

import { analyticsSummaryPath, type AnalyticsSummary } from '#types';
import { UserContext } from '@/providers/UserContext';
import { createDocumentStore } from '@/store';

import { analyticsAnswersRefetch } from './analyticsAnswersRefetch';
import { REFETCH_LAG_MAX_MILLIS } from './REFETCH_LAG_MAX_MILLIS';

export const useAnalyticsAnswersRefetch = (): void => {
  const { user } = useContext(UserContext);
  const uid = user?.uid;

  useEffect(() => {
    if (!uid) return;
    const store = createDocumentStore<AnalyticsSummary>(
      analyticsSummaryPath(uid, 'self'),
    );
    const refetch: {
      seen: string | null;
      timer: ReturnType<typeof setTimeout> | null;
    } = { seen: store.state.data?.computedAt ?? null, timer: null };
    const unsubscribe = subscribe(store.state, () => {
      const computedAt = store.state.data?.computedAt ?? null;
      if (computedAt === null || computedAt === refetch.seen) return;
      const baseline = refetch.seen === null;
      refetch.seen = computedAt;
      if (baseline || refetch.timer !== null) return;
      refetch.timer = setTimeout(() => {
        refetch.timer = null;
        analyticsAnswersRefetch.computedAt = refetch.seen;
      }, Math.random() * REFETCH_LAG_MAX_MILLIS);
    });
    void store.connect();
    return () => {
      unsubscribe();
      if (refetch.timer !== null) clearTimeout(refetch.timer);
      store.disconnect();
    };
  }, [uid]);
};
